import PropTypes from 'prop-types';
import React, { Component } from 'react';
import $ from 'jquery';
import moment from 'moment-timezone';
import { Field } from 'redux-form';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import classnames from 'classnames';
import compose from 'recompose/compose';
import branch from 'recompose/branch';
import renderNothing from 'recompose/renderNothing';

import { extractDays } from './helpers/schedules';
import withDateFormat from '../auth/utils/withDateFormat';
import withTimezone from '../auth/utils/withTimezone';

const inputPropTypes = {
  prefix: PropTypes.string,
  datepicker: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
  ]),
  schedules: PropTypes.shape({
    current: PropTypes.object,
    future: PropTypes.object,
  }),
  timezone: PropTypes.string,
  className: PropTypes.string,
};
const inputDefaultProps = {
  prefix: '',
  datepicker: null,
  schedules: {},
  timezone: '',
  className: '',
};

class FieldComponent extends Component {
  static contextTypes = {
    setValidationError: PropTypes.func,
  };

  componentDidMount() {
    const { datepicker, input, timezone, dateFormat } = this.props;
    if (!datepicker) return;

    const optionsFromProps = (typeof datepicker === 'object') ? datepicker : null;

    const today = moment.tz(timezone).toDate();
    const options = {
      autoclose: true,
      enableOnReadonly: false,
      todayHighlight: true,
      format: dateFormat.toLowerCase(),
      ...optionsFromProps,
    };

    if (input.name === 'schedules.current.endDate') {
      options.startDate = today;
    }

    const picker = this.input;
    this.$picker = $(picker);
    this.$picker.datepicker(options)
      .on('changeDate', (e) => {
        if (!e.date) {
          this.props.input.onChange(null);
          return;
        }

        this.props.input.onChange(e.date);
      })
      .on('clearDate', () => {
        this.props.input.value = null;
      });

    this.setValidationError();
  }

  componentWillReceiveProps(nextProps) {
    const { datepicker, input, schedules, dateFormat } = nextProps;

    if (!(datepicker && schedules)) return;

    const current = extractDays(schedules.current);
    const future = extractDays(schedules.future);

    if (
      this.props.schedules &&
      isEqual(current, extractDays(this.props.schedules.current)) &&
      isEqual(future, extractDays(this.props.schedules.future))
    ) {
      return;
    }

    const getMomentFromDay = day => (
      typeof day === 'string' ? moment(day, dateFormat) : moment(day)
    );

    const yesterday = day => (
      getMomentFromDay(day).subtract(1, 'day').toDate()
    );

    const tomorrow = day => (
      getMomentFromDay(day).add(1, 'day').toDate()
    );

    switch (input.name) {
      case 'schedules.current.startDate':
        if (current.endDate) {
          this.$picker.datepicker('setEndDate', yesterday(current.endDate));
        } else {
          this.$picker.datepicker('setEndDate', Infinity);
        }
        break;
      case 'schedules.current.endDate':
        this.$picker.datepicker('setStartDate', tomorrow(current.startDate));
        if (future.startDate) {
          this.$picker.datepicker('setEndDate', yesterday(future.startDate));
        } else {
          this.$picker.datepicker('setEndDate', Infinity);
        }
        break;
      case 'schedules.future.startDate':
        if (current.endDate) {
          this.$picker.datepicker('setStartDate', tomorrow(current.endDate));
        }
        if (future.endDate) {
          this.$picker.datepicker('setEndDate', future.endDate);
        } else {
          this.$picker.datepicker('setEndDate', Infinity);
        }
        break;
      case 'schedules.future.endDate':
        if (future.startDate) {
          this.$picker.datepicker('setStartDate', future.startDate);
        }
        break;
      default:
    }
  }

  componentDidUpdate() {
    this.setValidationError();
  }

  setValidationError() {
    const { meta: { touched, error }, input: { name } } = this.props;
    if (!this.context.setValidationError) return;
    if (touched && error) {
      this.context.setValidationError(name, error);
      return;
    }
    this.context.setValidationError(name);
  }

  render() {
    const { input, meta, prefix, className, placeholder, dateFormat } = this.props;

    if (input.value instanceof Date) {
      input.value = moment(input.value).format(dateFormat);

      // don't let redux form set a value based on the view value
      input.onBlur = noop;
    }

    return (
      <div className="u-position-relative">
        <div className="c-prefix u-inline-block" display-if={prefix}>{prefix}</div>
        <input
          {...input}
          {...omit(this.props, ['input', 'meta', 'timezone', 'datepicker', 'schedules', 'dateFormat'])}
          className={classnames(className, { 'c-input--prepend': prefix })}
          placeholder={(placeholder && !input.value) ? placeholder : ''}
          ref={(el) => { this.input = el; }}
        />
        <div className="c-input-error-message" display-if={meta.touched && meta.error}>{meta.error}</div>
      </div>
    );
  }

}
FieldComponent.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }).isRequired,
  ...inputPropTypes,
};
FieldComponent.defaultProps = inputDefaultProps;

const Input = ({ name, prefix, ...props }) => (
  <Field
    name={name}
    prefix={prefix}
    {...props}
    component={FieldComponent}
  />
);
Input.propTypes = {
  name: PropTypes.string.isRequired,
  ...inputPropTypes,
};
Input.defaultProps = inputDefaultProps;

export default compose(
  branch(
    props => !props.name,
    renderNothing,
  ),
  withTimezone,
  withDateFormat(),
)(Input);
